import type { EstadoAuth } from '../api/authApi';

type Props = {
  estado: EstadoAuth;
  onAtualizar?: () => void | Promise<void>;
  atualizando?: boolean;
};

/**
 * Tom da pílula: usa os tokens semânticos do sistema (mesmos do `.badge`),
 * não hex fixo — assim o indicador acompanha o tema claro/escuro.
 */
function descreverStatus(estado: EstadoAuth): {
  tom: 'ok' | 'atencao' | 'critico';
  rotulo: string;
  detalhe: string;
} {
  const d = estado.diasDesdeOnline;
  switch (estado.status) {
    case 'online':
      return {
        tom: 'ok',
        rotulo: 'Conectado',
        detalhe: 'Sincronizado com o servidor agora.',
      };
    case 'offline-ok':
      return {
        tom: 'atencao',
        rotulo: `Offline${d !== null ? ` há ${d}d` : ''}`,
        detalhe:
          'Funcionando normalmente. ' +
          (estado.diasParaModoLeitura !== null
            ? `Em ${estado.diasParaModoLeitura}d entra em modo só leitura se não reconectar.`
            : ''),
      };
    case 'offline-leitura':
      return {
        tom: 'atencao',
        rotulo: `Só leitura · offline há ${d ?? '?'}d`,
        detalhe:
          'App não conseguiu validar a assinatura. ' +
          (estado.diasParaBloqueio !== null
            ? `Em ${estado.diasParaBloqueio}d bloqueia se não reconectar.`
            : ''),
      };
    case 'offline-bloqueado':
      return {
        tom: 'critico',
        rotulo: 'Sem acesso',
        detalhe: 'Conecte à internet para revalidar a assinatura.',
      };
    // Só o app web chega aqui: sem servidor não há dado local para
    // continuar trabalhando, então o texto não promete nada.
    case 'sem-servidor':
      return {
        tom: 'critico',
        rotulo: 'Sem conexão',
        detalhe:
          'O servidor não está respondendo. Nada será salvo até a conexão voltar — ' +
          'reconecta sozinho assim que possível.',
      };
  }
}

export function StatusConexao({ estado, onAtualizar, atualizando = false }: Props) {
  const { tom, rotulo, detalhe } = descreverStatus(estado);
  return (
    <span className={`status-pill status-pill--${tom}`} title={detalhe}>
      <span className="status-pill__ponto" aria-hidden="true" />
      {/* O rótulo já diz o estado: a cor reforça, mas nunca é a única pista. */}
      <span>{rotulo}</span>
      <span className="sr-only">{detalhe}</span>
      {onAtualizar && (
        <button
          type="button"
          className="status-pill__acao"
          onClick={() => void onAtualizar()}
          disabled={atualizando}
          aria-label={atualizando ? 'Atualizando conexão' : 'Atualizar conexão'}
          data-tooltip-id="icon-tip"
        >
          <svg
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            style={{ animation: atualizando ? 'spin 0.9s linear infinite' : undefined }}
          >
            <path d="M23 4v6h-6" />
            <path d="M1 20v-6h6" />
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
          </svg>
        </button>
      )}
    </span>
  );
}
